/**
 * Оверлеи: диалог, поповер, меню, тултип, вкладки.
 *
 * Все на Radix: фокус-ловушка, возврат фокуса, `Escape`, порядок стрелок и
 * роли — то, что в самописных оверлеях ломается первым.
 */

import * as React from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import * as RadixDropdown from "@radix-ui/react-dropdown-menu";
import * as RadixPopover from "@radix-ui/react-popover";
import * as RadixTabs from "@radix-ui/react-tabs";
import * as RadixTooltip from "@radix-ui/react-tooltip";
import { X } from "lucide-react";

import { cn } from "@/shared/lib/cn";
import { IconButton } from "@/shared/ui/Button";

const SURFACE =
  "rounded-[var(--pt-radius-5)] border border-[var(--pt-border-default)] " +
  "bg-[var(--pt-bg-raised)] shadow-[var(--pt-shadow-2)]";

/* ── Диалог ─────────────────────────────────────────────────────────────── */

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Заголовок отвечает на вопрос «что произойдёт», а не «вы уверены?». */
  title: string;
  description?: string;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 480,
}: DialogProps): React.ReactElement {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-[var(--pt-z-overlay)] bg-[var(--pt-overlay-scrim)]" />
        <RadixDialog.Content
          style={{ width }}
          className={cn(
            "fixed left-1/2 top-1/2 z-[var(--pt-z-dialog)] max-h-[85vh] -translate-x-1/2 -translate-y-1/2",
            "overflow-auto p-[var(--pt-space-24)] focus:outline-none",
            SURFACE,
          )}
        >
          <div className="mb-[var(--pt-space-16)] flex items-start justify-between gap-[var(--pt-space-16)]">
            <div className="min-w-0">
              <RadixDialog.Title className="text-[var(--pt-fs-lg)] leading-[var(--pt-lh-lg)] font-semibold text-[var(--pt-text-primary)]">
                {title}
              </RadixDialog.Title>
              {description && (
                <RadixDialog.Description className="mt-[var(--pt-space-4)] text-[var(--pt-fs-sm)] leading-[var(--pt-lh-sm)] text-[var(--pt-text-secondary)]">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close asChild>
              <IconButton
                label="Закрыть"
                size="sm"
                icon={<X className="size-[var(--pt-icon)]" />}
              />
            </RadixDialog.Close>
          </div>

          {children}

          {footer && (
            <div className="mt-[var(--pt-space-24)] flex justify-end gap-[var(--pt-space-8)]">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/* ── Поповер ────────────────────────────────────────────────────────────── */

export interface PopoverProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  align?: "start" | "center" | "end";
  className?: string;
  /** Управляемый режим: нужен там, где окно открывается ещё и с клавиатуры. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function Popover({
  trigger,
  children,
  align = "start",
  className,
  open,
  onOpenChange,
}: PopoverProps): React.ReactElement {
  return (
    <RadixPopover.Root
      {...(open === undefined ? {} : { open })}
      {...(onOpenChange === undefined ? {} : { onOpenChange })}
    >
      <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          align={align}
          sideOffset={4}
          className={cn(
            "z-[var(--pt-z-dropdown)] p-[var(--pt-space-12)] focus:outline-none",
            SURFACE,
            className,
          )}
        >
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

/* ── Меню ───────────────────────────────────────────────────────────────── */

export interface MenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  /** Разделитель перед пунктом: разрушающие действия отделяются от обычных. */
  separatorBefore?: boolean;
}

export interface DropdownMenuProps {
  trigger: React.ReactNode;
  items: readonly MenuItem[];
  align?: "start" | "end";
}

export function DropdownMenu({
  trigger,
  items,
  align = "start",
}: DropdownMenuProps): React.ReactElement {
  return (
    <RadixDropdown.Root>
      <RadixDropdown.Trigger asChild>{trigger}</RadixDropdown.Trigger>
      <RadixDropdown.Portal>
        <RadixDropdown.Content
          align={align}
          sideOffset={4}
          className={cn("z-[var(--pt-z-dropdown)] min-w-[180px] p-[var(--pt-space-4)]", SURFACE)}
        >
          {items.map((item) => (
            <React.Fragment key={item.key}>
              {item.separatorBefore && (
                <RadixDropdown.Separator className="my-[var(--pt-space-4)] h-px bg-[var(--pt-border-subtle)]" />
              )}
              <RadixDropdown.Item
                disabled={item.disabled}
                onSelect={item.onSelect}
                className={cn(
                  "flex h-[var(--pt-row-h)] cursor-default select-none items-center gap-[var(--pt-space-8)]",
                  "rounded-[var(--pt-radius-2)] px-[var(--pt-space-8)] text-[var(--pt-font-ui)] outline-none",
                  "data-[highlighted]:bg-[var(--pt-bg-hover)]",
                  "data-[disabled]:text-[var(--pt-text-disabled)]",
                  item.danger
                    ? "text-[var(--pt-danger-text)] data-[highlighted]:bg-[var(--pt-danger-soft)]"
                    : "text-[var(--pt-text-primary)]",
                )}
              >
                {item.icon && <span className="text-[var(--pt-text-tertiary)]">{item.icon}</span>}
                <span className="flex-1 truncate">{item.label}</span>
                {item.shortcut && (
                  <span className="text-[var(--pt-fs-micro)] text-[var(--pt-text-tertiary)]">
                    {item.shortcut}
                  </span>
                )}
              </RadixDropdown.Item>
            </React.Fragment>
          ))}
        </RadixDropdown.Content>
      </RadixDropdown.Portal>
    </RadixDropdown.Root>
  );
}

/* ── Тултип ─────────────────────────────────────────────────────────────── */

export function TooltipProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  // Задержка 400 мс: подсказка не должна выскакивать при проходе мыши по тулбару
  return (
    <RadixTooltip.Provider delayDuration={400} skipDelayDuration={200}>
      {children}
    </RadixTooltip.Provider>
  );
}

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
}

export function Tooltip({ content, children, side = "top" }: TooltipProps): React.ReactElement {
  return (
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          sideOffset={6}
          className={cn(
            "z-[var(--pt-z-tooltip)] max-w-[280px] rounded-[var(--pt-radius-3)]",
            "bg-[var(--pt-text-primary)] px-[var(--pt-space-8)] py-[var(--pt-space-4)]",
            "text-[var(--pt-fs-xs)] leading-[var(--pt-lh-xs)] text-[var(--pt-bg-surface)]",
            "shadow-[var(--pt-shadow-2)]",
          )}
        >
          {content}
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}

/* ── Вкладки ────────────────────────────────────────────────────────────── */

export interface TabItem {
  value: string;
  label: string;
  badge?: React.ReactNode;
  content: React.ReactNode;
  /** Классы кнопки вкладки: ими вкладку прячут на широких экранах. */
  triggerClassName?: string;
}

export interface TabsProps {
  items: readonly TabItem[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  /**
   * Куда разложить сами вкладки.
   *
   * Горизонтальная лента упирается в ширину окна: русские названия длиннее
   * английских, и полтора десятка разделов в неё не помещаются — крайние
   * просто обрезаются, и о них нельзя догадаться. Такие наборы ставят
   * столбиком слева; то же решение записано в дизайн-системе для мастера
   * отчётов и по той же причине.
   */
  orientation?: "horizontal" | "vertical";
}

export function Tabs({
  items,
  defaultValue,
  value,
  onValueChange,
  className,
  orientation = "horizontal",
}: TabsProps): React.ReactElement {
  const vertical = orientation === "vertical";

  return (
    <RadixTabs.Root
      defaultValue={defaultValue ?? items[0]?.value}
      value={value}
      onValueChange={onValueChange}
      orientation={orientation}
      // h-full и min-h-0: вкладки занимают отведённую им высоту и передают
      // остаток содержимому. Без этого раздел с длинным списком рос по
      // содержимому, уезжал за нижний край и оставался без прокрутки —
      // а прокручивать было нечего, потому что высоты не было ни у кого
      className={cn("flex h-full min-h-0", vertical ? "flex-row" : "flex-col", className)}
    >
      <RadixTabs.List
        className={cn(
          vertical
            ? // Столбик прокручивается сам: разделов может стать и двадцать,
              // и упираться они должны в свою полосу, а не в край окна
              [
                "flex w-[200px] shrink-0 flex-col items-stretch gap-[2px] overflow-y-auto",
                "border-r border-[var(--pt-border-default)] pr-[var(--pt-space-8)]",
              ]
            : [
                "flex h-[var(--pt-tabs-h)] items-stretch gap-[var(--pt-space-16)]",
                "border-b border-[var(--pt-border-default)]",
              ],
        )}
      >
        {items.map((item) => (
          <RadixTabs.Trigger
            key={item.value}
            value={item.value}
            className={cn(
              "relative inline-flex items-center gap-[var(--pt-space-6)]",
              item.triggerClassName,
              "text-[var(--pt-font-ui)] text-[var(--pt-text-secondary)]",
              "transition-colors duration-[var(--pt-dur-micro)]",
              "hover:text-[var(--pt-text-primary)] focus-visible:outline-none",
              "focus-visible:shadow-[var(--pt-ring-inset)]",
              "data-[state=active]:text-[var(--pt-text-primary)]",
              vertical
                ? [
                    "justify-start rounded-[var(--pt-radius-3)] px-[var(--pt-space-8)]",
                    "py-[var(--pt-space-6)] text-left",
                    "hover:bg-[var(--pt-bg-hover)]",
                    "data-[state=active]:bg-[var(--pt-bg-selected)]",
                    // Полоса слева вместо подчёркивания: у столбика активную
                    // строку отмечают с той стороны, откуда её читают
                    "data-[state=active]:before:absolute data-[state=active]:before:inset-y-[4px]",
                    "data-[state=active]:before:left-0 data-[state=active]:before:w-[2px]",
                    "data-[state=active]:before:bg-[var(--pt-accent)]",
                  ]
                : [
                    "px-[2px]",
                    "data-[state=active]:after:absolute data-[state=active]:after:inset-x-0",
                    "data-[state=active]:after:-bottom-px data-[state=active]:after:h-[2px]",
                    "data-[state=active]:after:bg-[var(--pt-accent)]",
                  ],
            )}
          >
            {item.label}
            {item.badge}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>

      {items.map((item) => (
        <RadixTabs.Content
          key={item.value}
          value={item.value}
          // overflow-y-auto: до содержимого высота уже доведена, но доехать
          // до его низа было нельзя — панель обрезала лишнее молча, без
          // полосы. Вкладка с длинным списком полей просто заканчивалась
          // на краю окна. Панели со своей прокруткой внутри (лента) от
          // этого не меняются: их содержимое и так вписано в высоту
          className={cn(
            "flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto focus:outline-none",
            vertical ? "pl-[var(--pt-space-16)]" : "pt-[var(--pt-space-16)]",
          )}
        >
          {item.content}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  );
}
